import {
  Component, inject, ChangeDetectionStrategy, AfterViewInit, OnDestroy, ViewChild, ElementRef,
  NgZone, signal, computed, effect,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { OnboardingService } from '../api/onboarding.service';
import { formatProblemDetails } from '../shared/problem-details';
import { environment } from '../../environments/environment';
import { ThemeService } from '../api/theme.service';
import { BRAND } from '../../branding/brand.config';

// Vanta FOG background colors — numeric hex (Vanta/THREE convention, not CSS),
// so they're kept in sync with the theme by hand rather than via CSS variables.
const LOGIN_FOG_DARK = {
  highlightColor: 0xdae4ea,
  midtoneColor: 0x3ec82b,
  lowlightColor: 0x2b2261,
  baseColor: 0x000000,
};
const LOGIN_FOG_LIGHT = {
  highlightColor: 0xb6ecd9,
  midtoneColor: 0x7addc0,
  lowlightColor: 0xd7e2f2,
  baseColor: 0xeef6f2,
};

@Component({
  selector: 'app-login',
  standalone: true,
  template: `
    <div class="login-screen" #vantaBg>
      @if (brand.loginBackgroundImage) {
        <img class="login-bg" [src]="brand.loginBackgroundImage" alt="" />
      }
      <div class="login-card">
        <div class="logo">
          <img [src]="loginLogo" [alt]="brand.appName" width="80" height="80" />
        </div>
        <h1>{{ brand.appName }}</h1>

        @if (mode() === 'login') {
          <p class="subtitle">Secure Access Required</p>
          <button class="btn-login" (click)="auth.login()">
            {{ brand.loginCtaText }}
          </button>
          <span class="version">v{{ version }}</span>
        } @else if (done()) {
          <p class="subtitle">Account Created</p>
          <p class="reg-message">
            Your account has been created. Please log in to continue.
          </p>
          <button class="btn-login" (click)="auth.login()">{{ brand.loginCtaText }}</button>
        } @else {
          <p class="subtitle">Create New Account</p>
          <div class="reg-form">
            <label class="reg-field">
              <span>First name</span>
              <input type="text" autocomplete="given-name" maxlength="80"
                     [value]="firstName()" (input)="firstName.set($any($event.target).value)"
                     [disabled]="busy()" />
            </label>
            <label class="reg-field">
              <span>Last name</span>
              <input type="text" autocomplete="family-name" maxlength="80"
                     [value]="lastName()" (input)="lastName.set($any($event.target).value)"
                     [disabled]="busy()" />
            </label>
            <label class="reg-field">
              <span>Email</span>
              <input type="email" autocomplete="email" maxlength="160"
                     [value]="email()" (input)="email.set($any($event.target).value)"
                     [disabled]="busy()" />
            </label>
            <label class="reg-field">
              <span>Password</span>
              <input type="password" autocomplete="new-password" maxlength="128"
                     [value]="password()" (input)="password.set($any($event.target).value)"
                     [disabled]="busy()" />
            </label>
            <p class="reg-hint" [class.ok]="passwordValid()">
              At least 8 characters, with an uppercase letter, a number, and a special character.
            </p>
            <label class="reg-field">
              <span>Account name</span>
              <input type="text" autocomplete="off" maxlength="120"
                     [value]="accountName()" (input)="onAccountNameInput($any($event.target).value)"
                     [disabled]="busy()" />
            </label>
            <label class="reg-check">
              <input type="checkbox" [checked]="sendVerify()"
                     (change)="sendVerify.set($any($event.target).checked)" [disabled]="busy()" />
              <span>Send a confirmation email</span>
            </label>
          </div>
          @if (error()) { <p class="reg-error">{{ error() }}</p> }
          <div class="reg-actions">
            <button class="btn-text" [disabled]="busy()" (click)="backToLogin()">Back</button>
            <button class="btn-login" [disabled]="!formValid() || busy()" (click)="submit()">
              {{ busy() ? 'Creating…' : 'Create Account' }}
            </button>
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .login-screen {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100vh;
      overflow: hidden;
      background: var(--surface);
    }

    .login-bg {
      /* Fit tightly to viewport height and center horizontally, letterboxing the sides rather
         than cropping top/bottom — some machines (e.g. certain MacBook Pro displays) rendered
         this animated gif taller than the viewport when it was sized to fill both dimensions
         via object-fit: cover, which favors filling width over respecting height. Setting an
         explicit height with width: auto instead lets the browser derive width from the gif's
         intrinsic aspect ratio, so it can never exceed the viewport height. */
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      height: 100%;
      width: auto;
      max-width: none;
      z-index: 0;
    }

    .login-card {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      padding: 40px;
      width: min(380px, calc(100vw - 32px));
      max-height: calc(100vh - 32px);
      overflow-y: auto;
      background: var(--surface-75-opaque);
      border: 1px solid var(--outline);
      border-radius: var(--radius-lg);
      backdrop-filter: blur(20px);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      animation: fade-in 0.4s ease-out;
    }

    .logo {
      width: 80px;
      height: 80px;
      animation: glow 3s ease-in-out infinite;
      border-radius: 50%;
      overflow: hidden;
    }

    h1 {
      font-size: 1.6rem;
      color: var(--primary);
      letter-spacing: 0.12em;
    }

    .subtitle {
      font-size: 0.78rem;
      color: var(--on-surface-variant);
      text-transform: uppercase;
      letter-spacing: 0.1em;
    }

    .btn-login {
      margin-top: 8px;
      padding: 12px 32px;
      background: var(--primary);
      color: var(--on-primary);
      border: none;
      border-radius: var(--radius-md);
      font-family: 'Rajdhani', system-ui, sans-serif;
      font-size: 0.95rem;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      cursor: pointer;
      transition: background 0.2s, transform 0.1s;

      &:hover:not(:disabled) { background: var(--inverse-primary); }
      &:active:not(:disabled) { transform: scale(0.97); }
      &:disabled { opacity: 0.5; cursor: default; }
    }

    .btn-secondary {
      padding: 10px 28px;
      background: transparent;
      border: 1px solid var(--outline);
      color: var(--on-surface);
      border-radius: var(--radius-md);
      font-family: 'Rajdhani', system-ui, sans-serif;
      font-size: 0.9rem;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      cursor: pointer;
      transition: border-color 0.2s, color 0.2s;

      &:hover { border-color: var(--primary); color: var(--primary); }
    }

    .btn-text {
      background: none;
      border: none;
      color: var(--on-surface-variant);
      cursor: pointer;
      font-family: inherit;
      font-size: 0.85rem;
      &:hover:not(:disabled) { color: var(--on-surface); }
      &:disabled { opacity: 0.5; cursor: default; }
    }

    .reg-form {
      display: flex;
      flex-direction: column;
      gap: 12px;
      width: 100%;
      margin-top: 4px;
    }
    .reg-field {
      display: flex;
      flex-direction: column;
      gap: 5px;
      text-align: left;
    }
    .reg-field > span {
      font-size: 0.72rem;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--on-surface-variant);
    }
    .reg-field input {
      width: 100%;
      box-sizing: border-box;
      padding: 10px 12px;
      font-size: 0.95rem;
      color: var(--on-surface);
      background: var(--surface-variant, #1f2225);
      border: 1px solid var(--outline);
      border-radius: var(--radius-sm);
      outline: none;
    }
    .reg-field input:focus { border-color: var(--primary); }
    .reg-hint {
      margin: 0;
      text-align: left;
      font-size: 0.72rem;
      line-height: 1.4;
      color: var(--on-surface-variant);
    }
    .reg-hint.ok { color: var(--primary); }
    .reg-error {
      margin: 0;
      text-align: left;
      font-size: 0.82rem;
      line-height: 1.5;
      white-space: pre-line;
      color: var(--mat-sys-error, #f44336);
    }
    .reg-check {
      display: flex;
      align-items: center;
      gap: 8px;
      align-self: flex-start;
      font-size: 0.9rem;
      color: var(--on-surface);
      cursor: pointer;
    }
    .reg-check input {
      width: 16px;
      height: 16px;
      accent-color: var(--primary);
      cursor: pointer;
    }
    .reg-check input:disabled { cursor: default; }
    .reg-actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      width: 100%;
      margin-top: 4px;
    }
    .reg-message {
      margin: 0;
      text-align: center;
      font-size: 0.9rem;
      line-height: 1.5;
      color: var(--on-surface);
    }

    .version {
      margin-top: 4px;
      font-size: 0.7rem;
      color: var(--on-surface-variant);
      letter-spacing: 0.08em;
      opacity: 0.7;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements AfterViewInit, OnDestroy {
  protected readonly brand = BRAND;
  protected readonly loginLogo = BRAND.logoLargePath ?? BRAND.logoPath;
  protected readonly auth = inject(AuthService);
  private readonly zone = inject(NgZone);
  private readonly onboarding = inject(OnboardingService);
  protected readonly version = environment.buildVersion;

  // ── Registration ("Create New Account" — user is not authenticated yet) ──
  protected readonly mode = signal<'login' | 'register'>('login');
  protected readonly firstName = signal('');
  protected readonly lastName = signal('');
  protected readonly email = signal('');
  protected readonly password = signal('');
  protected readonly accountName = signal('');
  /** Once the user edits the account name we stop auto-filling it from the last name. */
  private readonly accountNameEdited = signal(false);
  protected readonly sendVerify = signal(true);
  protected readonly busy = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly done = signal(false);

  // Keep the account name defaulted to "{LastName} Group" until the user edits it.
  private readonly _defaultAccountName = effect(() => {
    const last = this.lastName().trim();
    if (!this.accountNameEdited()) this.accountName.set(last ? `${last} Group` : '');
  });

  /** Password policy: 8+ chars with an uppercase letter, a number, and a special character. */
  protected readonly passwordValid = computed(() => {
    const p = this.password();
    return p.length >= 8 && /[A-Z]/.test(p) && /[0-9]/.test(p) && /[^A-Za-z0-9]/.test(p);
  });
  protected readonly emailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email().trim()));
  protected readonly formValid = computed(() =>
    this.firstName().trim().length >= 1 &&
    this.lastName().trim().length >= 1 &&
    this.emailValid() &&
    this.passwordValid() &&
    this.accountName().trim().length >= 1
  );

  protected openRegister(): void {
    this.error.set(null);
    this.mode.set('register');
  }

  protected backToLogin(): void {
    this.error.set(null);
    this.mode.set('login');
  }

  protected onAccountNameInput(value: string): void {
    this.accountNameEdited.set(true);
    this.accountName.set(value);
  }

  protected async submit(): Promise<void> {
    if (!this.formValid() || this.busy()) return;
    this.busy.set(true);
    this.error.set(null);
    try {
      await firstValueFrom(this.onboarding.register({
        firstName: this.firstName().trim(),
        lastName: this.lastName().trim(),
        email: this.email().trim(),
        password: this.password(),
        accountName: this.accountName().trim(),
        sendVerifyEmail: this.sendVerify(),
      }));
      this.done.set(true);
    } catch (e) {
      this.error.set(formatProblemDetails(e, 'We couldn’t create your account.'));
    } finally {
      this.busy.set(false);
    }
  }

  @ViewChild('vantaBg') private vantaBg!: ElementRef<HTMLDivElement>;
  private vantaEffect: any = null;
  private readonly theme = inject(ThemeService);

  // Live theme changes while the login screen is up (e.g. "System" + an OS
  // theme flip) — the initial paint in ngAfterViewInit reads isDark() directly.
  private readonly _vantaTheme = effect(() => {
    const colors = this.theme.isDark() ? LOGIN_FOG_DARK : LOGIN_FOG_LIGHT;
    this.vantaEffect?.setOptions?.(colors);
  });

  ngAfterViewInit(): void {
    if (this.brand.loginBackgroundImage) return;
    this.zone.runOutsideAngular(() => {
      const vanta = (window as any).VANTA;
      if (!vanta?.FOG) return;
      this.vantaEffect = vanta.FOG({
        el: this.vantaBg.nativeElement,
        THREE: (window as any).THREE,
        mouseControls: true,
        touchControls: true,
        gyroControls: false,
        minHeight: 200,
        minWidth: 200,
        ...(this.theme.isDark() ? LOGIN_FOG_DARK : LOGIN_FOG_LIGHT),
      });
    });
  }

  ngOnDestroy(): void {
    this.vantaEffect?.destroy();
  }
}
